import { View, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";

export interface UpdateModalProps {
  current: string;
  latest: string;
  onLater: () => void;
  onRefresh: () => void;
}

/**
 * GitHub-style update dialog: header (icon + title + divider), a red→green version
 * diff, and a footer with a subtle "Later" and a green "Refresh".
 */
export function UpdateModal({ current, latest, onLater, onRefresh }: UpdateModalProps) {
  const { colors } = useTheme();
  return (
    <View style={[styles.card, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <MaterialCommunityIcons name="arrow-up-circle-outline" size={20} color={colors.primary} />
        <ThemedText type="subtitle" style={styles.title}>
          Update available
        </ThemedText>
      </View>

      <View style={styles.body}>
        <ThemedText style={[styles.message, { color: colors.textSecondary }]}>
          A newer version has been deployed. Refresh to get the latest.
        </ThemedText>
        <View style={styles.diff}>
          <ThemedText style={[styles.version, { color: colors.red.text }]}>{current}</ThemedText>
          <MaterialCommunityIcons name="arrow-right" size={15} color={colors.textSecondary} />
          <ThemedText style={[styles.version, styles.versionNew, { color: colors.green.background }]}>
            {latest}
          </ThemedText>
        </View>
      </View>

      <View style={[styles.footer, { borderTopColor: colors.border }]}>
        <Button text="Later" style={styles.later} textStyle={{ color: colors.textSecondary }} onPress={onLater} />
        <Button
          text="Refresh"
          style={[styles.refresh, { backgroundColor: colors.green.background }]}
          textStyle={{ color: "white" }}
          onPress={onRefresh}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    width: 380,
    maxWidth: "90%",
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 10,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  title: { fontSize: 16 },
  body: { paddingHorizontal: 16, paddingVertical: 16, gap: 14 },
  message: { fontSize: 14, lineHeight: 20 },
  diff: { flexDirection: "row", alignItems: "center", gap: 8 },
  version: { fontSize: 15, fontWeight: "600", fontVariant: ["tabular-nums"] },
  versionNew: { fontWeight: "700" },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  later: { height: 35, backgroundColor: "transparent", paddingHorizontal: 14, paddingVertical: 8 },
  refresh: { height: 35, paddingHorizontal: 16, paddingVertical: 8, borderRadius: 6 },
});
